import React from 'react';

interface TermsAndConditionsProps {
  appName?: string;
  onClose: () => void;
}

const TermsAndConditionsVamosly: React.FC<TermsAndConditionsProps> = ({ appName = 'Vamosly', onClose }) => {
  return (
    <div className="fixed inset-0 z-[300] bg-black/95 backdrop-blur-xl animate-in fade-in duration-300 overflow-y-auto">
      <button 
        onClick={onClose}
        className="fixed top-8 right-8 z-[310] size-14 bg-white text-black flex items-center justify-center hover:bg-primary hover:text-white transition-all group"
      >
        <span className="material-symbols-outlined transform group-hover:rotate-90 transition-transform">close</span>
      </button>

      <div className="max-w-[900px] mx-auto px-6 py-32 text-white/80">
        <h1 className="text-5xl lg:text-7xl font-black uppercase tracking-tighter leading-none mb-4 italic text-white flex flex-col gap-2">
          <span>Terms of</span>
          <span>Service</span>
        </h1>
        <p className="text-xl text-primary font-bold tracking-widest uppercase mb-4">
          {appName}
        </p>
        <p className="text-sm font-bold tracking-widest uppercase mb-12 border-b border-white/10 pb-6 text-white/40">
          Effective date: 30 July 2026
        </p>

        <div className="space-y-12 font-light leading-relaxed text-lg">
          <section className="space-y-4">
            <p>
              These Terms of Service ("Terms") govern your access to and use of {appName} (the "App"), provided by <strong className="text-white">T.H.E Apps</strong>, registered at <strong className="text-white">94 9th Avenue, Parkmore</strong> ("T.H.E Apps," "we," "us," or "our"). By creating an account or using the App, you agree to be bound by these Terms. If you do not agree, do not use the App.
            </p>
          </section>

          <section>
            <h2 className="text-3xl font-bold text-white mb-6">1. Eligibility</h2>
            <p>
              You must be at least 13 years old to create an account and use {appName}. By using the App, you confirm that you meet this requirement. If you are under the age of majority in your jurisdiction, you confirm that a parent or legal guardian has reviewed and agreed to these Terms on your behalf.
            </p>
          </section>

          <section>
            <h2 className="text-3xl font-bold text-white mb-6">2. The Service</h2>
            <div className="space-y-4">
              <p>
                {appName} helps you organise padel sessions, competitions, and leagues, including generating player pairings and rotations (Americano, Mexicano, and Standard Doubles formats), tracking scores and standings, and maintaining player statistics and session history.
              </p>
              <p>
                We may add, change, or remove features of the App at any time, and we may suspend or discontinue the App, in whole or in part, without liability to you, though we will try to give reasonable notice where practical.
              </p>
            </div>
          </section>

          <section>
            <h2 className="text-3xl font-bold text-white mb-6">3. Accounts</h2>
            <ul className="list-disc pl-6 space-y-2">
              <li>You are responsible for maintaining the confidentiality of your account credentials and for all activity that occurs under your account.</li>
              <li>You must provide accurate information when creating your account and keep it up to date.</li>
              <li>You are responsible for any content or data you submit through your account, including player names and game data you enter for other players.</li>
              <li>You may delete your account at any time from within the App's Settings.</li>
              <li>We may suspend or terminate your account if we reasonably believe you have violated these Terms.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-3xl font-bold text-white mb-6">4. Acceptable Use</h2>
            <p className="mb-4">When using {appName}, you agree not to:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Use the App for any unlawful purpose or in violation of any applicable law or regulation</li>
              <li>Impersonate any person or entity, or misrepresent your affiliation with a person or entity</li>
              <li>Enter offensive, abusive, defamatory, or harassing content as a player name or elsewhere in the App</li>
              <li>Attempt to gain unauthorised access to the App, other users' accounts, or our systems</li>
              <li>Interfere with or disrupt the App's operation, including through malware, scraping, or automated access not authorised by us</li>
              <li>Use the App to collect or harvest personal information about other users beyond what is necessary for organising your own games, competitions, or leagues</li>
              <li>Reverse engineer, decompile, or attempt to extract the source code of the App, except to the extent such restriction is prohibited by applicable law</li>
            </ul>
          </section>

          <section>
            <h2 className="text-3xl font-bold text-white mb-6">5. User Content</h2>
            <div className="space-y-4">
              <p>
                You retain ownership of the content you submit to the App (such as player names, session configurations, and league details) ("User Content"). By submitting User Content, you grant T.H.E Apps a non-exclusive, worldwide, royalty-free licence to host, store, reproduce, and display that content solely as necessary to operate and provide the App to you and other participants in your sessions, competitions, or leagues.
              </p>
              <p>
                You are solely responsible for the accuracy and legality of any User Content you submit, including obtaining any consent needed from other players before adding their names or data to the App.
              </p>
              <p>
                We may remove User Content that we reasonably believe violates these Terms or applicable law.
              </p>
            </div>
          </section>

          <section>
            <h2 className="text-3xl font-bold text-white mb-6">6. Leagues and Multiplayer Features</h2>
            <p>
              When you create or join a league, competition, or session, certain information (such as player names, scores, and standings) is shared with other participants of that league, competition, or session, as this is core to how the App functions. League organisers are responsible for managing membership, invite codes, and round outcomes within their leagues.
            </p>
          </section>

          <section>
            <h2 className="text-3xl font-bold text-white mb-6">7. Intellectual Property</h2>
            <p>
              The App, including its design, features, logos, and underlying software, is owned by T.H.E Apps or our licensors and is protected by intellectual property laws. Except for the limited licence to use the App as intended, these Terms do not grant you any rights to our trademarks, logos, or other brand features.
            </p>
          </section>

          <section>
            <h2 className="text-3xl font-bold text-white mb-6">8. Third-Party Services</h2>
            <p>
              The App may rely on third-party infrastructure and service providers to operate (such as hosting and backend services). We are not responsible for the availability or performance of third-party services outside our control.
            </p>
          </section>

          <section>
            <h2 className="text-3xl font-bold text-white mb-6">9. Disclaimers</h2>
            <div className="space-y-4">
              <p>
                The App is provided "as is" and "as available," without warranties of any kind, whether express or implied, including implied warranties of merchantability, fitness for a particular purpose, and non-infringement. We do not warrant that the App will be uninterrupted, error-free, or free of harmful components, or that any pairing, scoring, or ranking calculations will be free of defects.
              </p>
              <p>
                {appName} is a tool to help organise recreational and competitive padel activity. We are not responsible for the conduct of players, the outcome of any game, injuries sustained while playing padel, or disputes between players arising from sessions, competitions, or leagues organised through the App.
              </p>
            </div>
          </section>

          <section>
            <h2 className="text-3xl font-bold text-white mb-6">10. Limitation of Liability</h2>
            <div className="space-y-4">
              <p>
                To the fullest extent permitted by applicable law, T.H.E Apps and its officers, employees, and agents will not be liable for any indirect, incidental, special, consequential, or punitive damages, or any loss of data, use, goodwill, or profits, arising from or related to your use of the App, even if we have been advised of the possibility of such damages.
              </p>
              <p>
                To the fullest extent permitted by applicable law, our total aggregate liability to you for any claim arising from or related to the App will not exceed the amount you paid us, if any, for use of the App in the twelve (12) months preceding the claim.
              </p>
              <p>
                Nothing in these Terms limits or excludes any liability that cannot be limited or excluded under applicable law.
              </p>
            </div>
          </section>

          <section>
            <h2 className="text-3xl font-bold text-white mb-6">11. Indemnification</h2>
            <p>
              You agree to indemnify and hold harmless T.H.E Apps and its officers, employees, and agents from any claims, damages, losses, or expenses (including reasonable legal fees) arising from your violation of these Terms, your User Content, or your use of the App.
            </p>
          </section>

          <section>
            <h2 className="text-3xl font-bold text-white mb-6">12. Termination</h2>
            <p>
              We may suspend or terminate your access to the App at any time, with or without cause or notice, including if we reasonably believe you have violated these Terms. You may stop using the App and delete your account at any time. Sections of these Terms that by their nature should survive termination (including Sections 5, 7, 9, 10, and 11) will survive.
            </p>
          </section>

          <section>
            <h2 className="text-3xl font-bold text-white mb-6">13. Changes to These Terms</h2>
            <p>
              We may update these Terms from time to time. If we make material changes, we will notify you by updating the effective date at the top of these Terms and, where appropriate, through the App. Your continued use of the App after changes take effect constitutes acceptance of the revised Terms.
            </p>
          </section>

          <section>
            <h2 className="text-3xl font-bold text-white mb-6">14. Governing Law</h2>
            <p>
              These Terms are governed by the laws of the Republic of South Africa, without regard to its conflict of law principles. Any dispute arising from these Terms or your use of the App will be subject to the exclusive jurisdiction of the courts of South Africa, except where applicable consumer protection law grants you the right to bring proceedings in your own country of residence.
            </p>
          </section>

          <section>
            <h2 className="text-3xl font-bold text-white mb-6">15. Contact Us</h2>
            <p className="mb-4">
              If you have any questions about these Terms, contact us at:
            </p>
            <div className="bg-white/5 p-6 border border-white/10 rounded-sm">
              <strong className="text-white text-lg block mb-2">T.H.E Apps</strong>
              <p className="mb-2">94 9th Avenue, Parkmore</p>
              <a href="mailto:rob@theapps.io" className="text-primary hover:underline">Email: rob@theapps.io</a>
            </div>
          </section>

        </div>
      </div>
    </div>
  );
};

export default TermsAndConditionsVamosly;
